import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  isBootSplashFinished,
  shouldUseLogoSplash,
  subscribeBootSplashFinished,
  subscribeBootSplashStarted,
} from 'pages/components/Loader/BootSplash';
import { selectUserEmail } from 'pages/Hospitality/redux';
import { selectHotelData } from 'pages/redux';
import { useAppSelector } from 'redux/hooks';
import { RootState } from 'redux/store';

type BubbleVisibility = 'hide' | 'show';

const getChatwootSdk = () => (window as any).$chatwoot;

/** Signed-in contact we send so Chatwoot pre-chat fields can stay empty. */
export type ChatwootContactIdentity = {
  email?: string;
  name?: string;
  propertyName?: string;
};

let lastChatwootContactIdentity: ChatwootContactIdentity | null = null;

const applyChatwootContactIdentity = (identity: ChatwootContactIdentity) => {
  try {
    const chatwoot = getChatwootSdk();
    if (!chatwoot) return;
    const email = String(identity.email || '').trim();
    const name = String(identity.name || '').trim();
    const propertyName = String(identity.propertyName || '').trim();
    if (!email && !propertyName) return;
    // Email is the stable contact id — inbox then skips asking for it.
    if (email) {
      chatwoot.setUser?.(email, {
        email,
        ...(name ? { name } : {}),
        ...(propertyName ? { company_name: propertyName } : {}),
      });
    }
    if (propertyName) {
      chatwoot.setCustomAttributes?.({
        property_name: propertyName,
      });
    }
  } catch (e) {
    // SDK missing or mid-load — safe to ignore
  }
};

const whenChatwootReady = (run: () => void) => {
  if (getChatwootSdk()) {
    run();
    return;
  }
  const onChatwootReady = () => {
    window.removeEventListener('chatwoot:ready', onChatwootReady);
    run();
  };
  window.addEventListener('chatwoot:ready', onChatwootReady);
};

/** Push email + property name onto the Chatwoot contact (waits for SDK). */
export const identifyChatwootContact = (identity: ChatwootContactIdentity) => {
  lastChatwootContactIdentity = identity;
  whenChatwootReady(() => applyChatwootContactIdentity(identity));
};

/** Drop the identified contact on sign-out so the next session is anonymous. */
export const resetChatwootContact = () => {
  lastChatwootContactIdentity = null;
  try {
    getChatwootSdk()?.reset?.();
  } catch (e) {
    // SDK missing or mid-load — safe to ignore
  }
};

/** Toggle Chatwoot bubble if the SDK is already on window. */
export const setChatwootBubbleVisibility = (visibility: BubbleVisibility) => {
  try {
    getChatwootSdk()?.toggleBubbleVisibility?.(visibility);
  } catch (e) {
    // SDK missing or mid-load — safe to ignore
  }
};

/** Sign-in screens that keep the Chatwoot bubble. */
export const isChatwootSignInPath = (pathname: string): boolean => (
  pathname === '/' || pathname === '/signin' || pathname === '/pricing'
);

/** Settings Support tab — /hospitality/settings/support and legacy /settings/support. */
export const isChatwootSupportPath = (pathname: string): boolean => {
  const parts = pathname.split('/').filter(Boolean);
  const settingsIdx = parts.indexOf('settings');
  return settingsIdx >= 0 && parts[settingsIdx + 1] === 'support';
};

export const shouldShowChatwootBubble = (
  pathname: string,
  isLogoSplashVisible: boolean,
): boolean => {
  if (isLogoSplashVisible) return true;
  return isChatwootSignInPath(pathname) || isChatwootSupportPath(pathname);
};

/**
 * Hide the bubble now, and again when Chatwoot finishes loading.
 * Needed on refresh: SDK is async in index.html, so $chatwoot is often
 * missing on first mount and a one-shot hide does nothing.
 * Returns cleanup that removes the ready listener and shows the bubble again.
 */
export const hideChatwootBubbleForPage = (): (() => void) => {
  setChatwootBubbleVisibility('hide');

  const onChatwootReady = () => {
    setChatwootBubbleVisibility('hide');
  };
  window.addEventListener('chatwoot:ready', onChatwootReady);

  return () => {
    window.removeEventListener('chatwoot:ready', onChatwootReady);
    setChatwootBubbleVisibility('show');
  };
};

/** Open the Chatwoot thread; waits for SDK if it is still loading. */
export const openChatwootWidget = () => {
  const open = () => {
    // Re-apply identity first so the pre-chat form already has email / property.
    if (lastChatwootContactIdentity) {
      applyChatwootContactIdentity(lastChatwootContactIdentity);
    }
    try {
      getChatwootSdk()?.toggle?.('open');
    } catch (e) {
      // SDK missing or mid-load — safe to ignore
    }
  };
  whenChatwootReady(open);
};

const applyChatwootBubbleForRoute = (
  pathname: string,
  isLogoSplashVisible: boolean,
) => {
  const show = shouldShowChatwootBubble(pathname, isLogoSplashVisible);
  setChatwootBubbleVisibility(show ? 'show' : 'hide');
};

/**
 * Show Chatwoot only on Support, sign-in, and the post–sign-in logo splash.
 * Listens for chatwoot:ready so a late SDK load does not leave the bubble up.
 */
export const ChatwootBubbleGate = function ChatwootBubbleGate() {
  const { pathname } = useLocation();
  const userEmail = useAppSelector(selectUserEmail);
  // Local select — avoid importing redux/global (logout imports this file).
  const username = useAppSelector((state: RootState) => state.global.username);
  const hotelData = useAppSelector(selectHotelData);
  const [logoSplashVisible, setLogoSplashVisible] = useState(
    () => shouldUseLogoSplash() && !isBootSplashFinished(),
  );

  useEffect(() => {
    const unsubStart = subscribeBootSplashStarted(() => setLogoSplashVisible(true));
    const unsubEnd = subscribeBootSplashFinished(() => setLogoSplashVisible(false));
    setLogoSplashVisible(shouldUseLogoSplash() && !isBootSplashFinished());
    return () => {
      unsubStart();
      unsubEnd();
    };
  }, []);

  useEffect(() => {
    const apply = () => applyChatwootBubbleForRoute(pathname, logoSplashVisible);
    apply();
    window.addEventListener('chatwoot:ready', apply);
    return () => window.removeEventListener('chatwoot:ready', apply);
  }, [pathname, logoSplashVisible]);

  useEffect(() => {
    const email = String(userEmail || '').trim();
    const propertyName = String(hotelData?.name || '').trim();
    if (!email && !propertyName) return;
    identifyChatwootContact({
      email,
      name: String(username || '').trim(),
      propertyName,
    });
  }, [userEmail, username, hotelData?.name]);

  return null;
};
